/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import { UserPlus, Wallet, Phone, ArrowRight, ShieldCheck, Globe, Zap } from "lucide-react";
import SEO from "@/components/site/SEO";

const STEPS = [
  { icon: UserPlus, title: "1 — Create your free account", body: "Sign up with your email, verify your inbox and complete your profile. Takes 60 seconds. No credit card needed." },
  { icon: Wallet, title: "2 — Top up your wallet", body: "Choose a top-up amount and pay with any UK or international card via Stripe Checkout. Your closed-loop GBP balance is ready instantly." },
  { icon: Phone, title: "3 — Call any number worldwide", body: "Dial directly from the OneDial dialer — or save a contact for one-tap calling. Pay per-second from your wallet at our published rate." },
];

const BENEFITS = [
  { icon: Zap, title: "Per-second billing", body: "Pay only for the time you talk. No connection fees. No rounding tricks." },
  { icon: Globe, title: "50+ destinations", body: "Africa, Europe, North America and the Middle East — at diaspora-friendly rates." },
  { icon: ShieldCheck, title: "Safe and regulated", body: "Card payments via Stripe (FCA-regulated). Your wallet is safeguarded in a UK bank account." },
];

export default function HowItWorks() {
  return (
    <div className="bg-brand-bgSoft text-brand-ink" data-testid="how-it-works-page">
      <SEO title="How OneDial Africa works" path="/how-it-works"
        description="From signup to your first call in under 5 minutes. Here's exactly how OneDial Africa works." />

      <section className="bg-brand-ink text-white">
        <div className="container-page py-16 lg:py-24">
          <p className="label-eyebrow text-brand-green">How it works</p>
          <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl max-w-3xl">From sign-up to your first call in under 5 minutes</h1>
          <p className="mt-4 text-white/70 max-w-2xl">No SIM swap. No hidden fees. No tedious paperwork. The diaspora has had enough — OneDial Africa is the calling card reimagined for 2026.</p>
        </div>
      </section>

      <section className="container-page py-16 grid md:grid-cols-3 gap-6">
        {STEPS.map(({ icon: Icon, title, body }, i) => (
          <div key={title} className="rounded-2xl border border-brand-line bg-white p-6" data-testid={`hiw-step-${i+1}`}>
            <div className="h-12 w-12 rounded-xl bg-brand-greenSoft text-brand-greenDark flex items-center justify-center"><Icon className="h-6 w-6" /></div>
            <h3 className="mt-4 font-display font-extrabold text-lg">{title}</h3>
            <p className="mt-2 text-sm text-brand-muted">{body}</p>
          </div>
        ))}
      </section>

      <section className="bg-white border-y border-brand-line">
        <div className="container-page py-16">
          <p className="label-eyebrow">Why OneDial?</p>
          <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl">Built for the African diaspora</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {BENEFITS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-brand-line bg-brand-bgSoft p-6">
                <Icon className="h-6 w-6 text-brand-greenDark" />
                <h3 className="mt-3 font-display font-bold text-lg">{title}</h3>
                <p className="mt-2 text-sm text-brand-muted">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-page py-16">
        <div className="rounded-3xl bg-brand-ink text-white p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-overlay opacity-30" />
          <div className="relative max-w-2xl">
            <h2 className="font-display font-black text-3xl sm:text-4xl">Ready in 60 seconds</h2>
            <p className="mt-3 text-white/70">Open a free OneDial Africa account today. Get your first call started before your tea cools.</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hiw-cta-signup" className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover px-6 py-3 text-base font-semibold shadow-sm">
                Create my free account <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/rates" className="text-sm font-semibold text-white/80 hover:text-white">See the rates →</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
